import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { RichTextEditor } from './RichTextEditor';
import { GripVertical, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

export const DraggableSection = ({ 
  id, 
  section, 
  onUpdate, 
  onDelete, 
  isCustom = false,
  isPro = false 
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isPro });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-4">
      <Card className={`border ${isDragging ? 'border-primary shadow-lg' : 'border-stone-200'} bg-white`}>
        {/* Section Header */}
        <div className="flex items-center gap-2 p-3 border-b border-stone-100 bg-stone-50/50">
          {isPro && (
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-stone-200 rounded transition-colors"
              title="Drag to reorder"
            >
              <GripVertical className="w-4 h-4 text-stone-400" />
            </button>
          )}
          
          <Input
            value={section.title}
            onChange={(e) => onUpdate({ ...section, title: e.target.value })}
            className="flex-1 h-8 bg-transparent border-0 font-semibold text-stone-800 focus:ring-0 px-0"
            placeholder="Section Title"
            disabled={!isPro && !isCustom}
          />

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 hover:bg-stone-200 rounded transition-colors"
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? (
              <ChevronDown className="w-4 h-4 text-stone-500" />
            ) : (
              <ChevronUp className="w-4 h-4 text-stone-500" />
            )}
          </button>

          {(isCustom || isPro) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(id)}
              className="h-8 w-8 p-0 text-stone-400 hover:text-red-500 hover:bg-red-50"
              title="Delete section"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Section Content */}
        {!isCollapsed && (
          <div className="p-4">
            {isPro ? (
              <RichTextEditor
                content={section.content}
                onChange={(content) => onUpdate({ ...section, content })}
                placeholder="Enter section content..."
              />
            ) : (
              <textarea
                value={section.content.replace(/<[^>]*>/g, '')}
                onChange={(e) => onUpdate({ ...section, content: e.target.value })}
                className="w-full min-h-[120px] p-3 border border-stone-200 rounded-lg resize-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 text-sm"
                placeholder="Enter section content..."
              />
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
